/**
 * "Filter by column" — picked, not typed.
 *
 * The bar itself holds only what is applied, as chips. Adding one is three
 * steps in a small popover: the column, then the operators THAT column can
 * answer, then a box for the value. A date offers "is before", a boolean
 * offers "is true", and neither offers the other.
 *
 * It used to be one box you wrote `email contains ada` into, with the chips
 * beside the caret. Sharing a line between applied filters and a half-written
 * one put the cursor somewhere different from where the text appeared, and
 * reading the bar meant reading two things at once.
 *
 * Clicking a chip reopens it on its value; the ✕ takes it off; the word
 * between two chips is the join and flips all of them between "all of these"
 * and "any of these". ⌘K opens the popover from anywhere on the screen.
 *
 * What an operator MEANS is the database's answer, not this file's: the clauses
 * are built and checked on the server against the column's real type, so an
 * operator a type cannot answer comes back refused rather than quietly
 * comparing "9" with "100" as text.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppDatabaseColumn, AppDatabaseRowFilter } from '@back4app2/sdk';
import { DsButton } from '../../components/ds';
import { isNumericType, shortTypeName } from './cells';
import { ChevronRightIcon, SearchIcon } from '../../components/ds/icons';
import { Popover, PopoverItem, SearchField, SectionLabel } from './controls';
import { useDismiss } from './useDismiss';

export type ActiveFilter = AppDatabaseRowFilter;
export type FilterJoin = 'and' | 'or';

/** Which operators a column's type can answer. */
type Family = 'text' | 'number' | 'date' | 'bool' | 'other';

const familyOf = (dataType: string): Family => {
  const type = shortTypeName(dataType);

  if (/^bool/.test(type)) return 'bool';
  if (isNumericType(type)) return 'number';
  if (/^(timestamp|date|time)/.test(type)) return 'date';
  if (/^(text|varchar|char|citext|name)/.test(type)) return 'text';

  return 'other';
};

interface Operator {
  id: ActiveFilter['operator'];
  /** What the chip and the menu say. */
  label: string;
  needsValue: boolean;
  /** Absent means every type. */
  families?: Family[];
  /** What the value box says it wants. */
  hint?: string;
}

/**
 * The labels read as English on purpose — "is after" rather than ">". This is
 * a filter bar, not a SQL prompt; the console is one floor up for that.
 */
const OPERATORS: Operator[] = [
  { id: 'contains', label: 'contains', needsValue: true },
  { id: 'equals', label: 'is', needsValue: true },
  { id: 'notEquals', label: 'is not', needsValue: true },
  { id: 'startsWith', label: 'starts with', needsValue: true, families: ['text', 'other'] },
  { id: 'endsWith', label: 'ends with', needsValue: true, families: ['text', 'other'] },
  { id: 'gt', label: 'is greater than', needsValue: true, families: ['number'], hint: '100' },
  { id: 'gte', label: 'is at least', needsValue: true, families: ['number'], hint: '100' },
  { id: 'lt', label: 'is less than', needsValue: true, families: ['number'], hint: '100' },
  { id: 'lte', label: 'is at most', needsValue: true, families: ['number'], hint: '100' },
  { id: 'gt', label: 'is after', needsValue: true, families: ['date'], hint: '2026-01-31' },
  { id: 'lt', label: 'is before', needsValue: true, families: ['date'], hint: '2026-01-31' },
  { id: 'isTrue', label: 'is true', needsValue: false, families: ['bool'] },
  { id: 'isFalse', label: 'is false', needsValue: false, families: ['bool'] },
  { id: 'isNull', label: 'is empty', needsValue: false },
  { id: 'isNotNull', label: 'is not empty', needsValue: false },
];

const operatorsFor = (dataType: string) => {
  const family = familyOf(dataType);

  return OPERATORS.filter(operator => !operator.families || operator.families.includes(family));
};

/**
 * The operator a chip was made with — looked up by the column too, because two
 * types share an id under different names (`gt` is "is greater than" on a
 * number and "is after" on a date).
 */
const operatorOf = (filter: ActiveFilter, column?: AppDatabaseColumn) => {
  const allowed = column ? operatorsFor(column.dataType) : OPERATORS;

  return allowed.find(candidate => candidate.id === filter.operator)
    || OPERATORS.find(candidate => candidate.id === filter.operator);
};

/** `email contains ada`, `total is at least 100`, `deleted_at is empty`. */
export const describeFilter = (filter: ActiveFilter, columns?: AppDatabaseColumn[]) => {
  const column = columns?.find(candidate => candidate.name === filter.column);
  const operator = operatorOf(filter, column);
  const label = operator?.label || filter.operator;

  return operator?.needsValue === false
    ? `${filter.column} ${label}`
    : `${filter.column} ${label} ${filter.value ?? ''}`.trim();
};

/** Where the popover is: picking a column, an operator, or writing a value. */
type Step =
  | { at: 'column' }
  | { at: 'operator'; column: AppDatabaseColumn }
  | { at: 'value'; column: AppDatabaseColumn; operator: Operator; editing?: number };

const FilterInput = ({ columns, filters, join, isCompact, onChange, onJoin }: {
  columns: AppDatabaseColumn[],
  filters: ActiveFilter[],
  /** How the chips are joined — all of them, or any of them. */
  join: FilterJoin,
  /** A phone, where the bar shares its line with two other controls. */
  isCompact?: boolean,
  onChange: (filters: ActiveFilter[]) => void,
  onJoin: (join: FilterJoin) => void,
}) => {
  const [step, setStep] = useState<Step | null>(null);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => setStep(null), []);
  useDismiss(rootRef, !!step, close);

  const openAtColumn = useCallback(() => {
    setSearch('');
    setStep({ at: 'column' });
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openAtColumn();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openAtColumn]);

  // Each step owns one field, and the hand is already on the keyboard — so the
  // field that just appeared is the one that has the caret.
  useEffect(() => {
    if (step?.at === 'column') {
      window.setTimeout(() => searchRef.current?.focus(), 0);
    }
    if (step?.at === 'value') {
      window.setTimeout(() => {
        valueRef.current?.focus();
        valueRef.current?.select();
      }, 0);
    }
  }, [step]);

  const put = (filter: ActiveFilter, editing?: number) => {
    onChange(typeof editing === 'number'
      ? filters.map((current, index) => (index === editing ? filter : current))
      : [...filters, filter]);
    setStep(null);
    setDraft('');
  };

  const pickOperator = (column: AppDatabaseColumn, operator: Operator, editing?: number) => {
    // An operator that takes no value IS the whole filter, so picking it
    // applies it instead of opening a box with nothing to write in.
    if (!operator.needsValue) {
      put({ column: column.name, operator: operator.id }, editing);
      return;
    }

    setStep({ at: 'value', column, operator, editing });
  };

  const term = search.trim().toLowerCase();
  const shown = term
    ? columns.filter(column => column.name.toLowerCase().includes(term))
    : columns;

  return (
    <div className="relative flex-1 min-w-0" ref={rootRef}>
      {/* A bar, not a box: everything in it is already applied, and what is
          being written lives in the popover. `cursor: text` because it is
          still where you go to add one. */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Filter rows"
        aria-expanded={!!step}
        onClick={event => {
          // Only the empty part of the bar opens it — a chip and its ✕ have
          // their own jobs.
          if (event.target === event.currentTarget) {
            openAtColumn();
          }
        }}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openAtColumn();
          }
        }}
        className={[
          'b4-focus flex flex-wrap items-center gap-1.5 w-full min-h-[40px] lg:min-h-[38px] px-2.5 py-1 cursor-text',
          'rounded-control border bg-navy-800 transition-colors duration-[120ms]',
          step ? 'border-action shadow-[0_0_0_3px_rgba(32,138,236,.25)]' : 'border-navy-400 hover:border-navy-300',
        ].join(' ')}
      >
        <span className="flex-none text-navy-300 pointer-events-none"><SearchIcon size={16} /></span>

        {filters.map((filter, index) => (
          <span key={`${filter.column}-${filter.operator}-${index}`} className="flex-none inline-flex items-center gap-1.5">
            {/* The word between two chips is the join, and clicking it flips it
                for the whole row: one filter bar means one question, and "all
                of these" and "any of these" are the two it can ask. */}
            {index ? (
              <button
                type="button"
                onClick={() => onJoin(join === 'and' ? 'or' : 'and')}
                title={join === 'and' ? 'Every filter has to match — click for any' : 'Any filter may match — click for all'}
                aria-label={`Joining filters with ${join}. Change`}
                className="b4-focus flex-none h-[22px] px-1.5 rounded-[5px] font-courrier text-ds-115 uppercase tracking-[0.08em] text-navy-300 hover:text-white hover:bg-navy-600"
              >
                {join}
              </button>
            ) : null}
            <span className="flex-none inline-flex items-center h-[22px] rounded-[5px] bg-action/15 border border-action/40 font-courrier text-ds-115 text-action-soft">
              <button
                type="button"
                onClick={() => {
                  const column = columns.find(candidate => candidate.name === filter.column);
                  const operator = operatorOf(filter, column);

                  if (!column || !operator) {
                    return;
                  }
                  setDraft(filter.value || '');
                  setStep(operator.needsValue
                    ? { at: 'value', column, operator, editing: index }
                    : { at: 'operator', column });
                }}
                title="Edit this filter"
                className="b4-focus max-w-[30ch] truncate pl-2 pr-1 h-full rounded-l-[5px] hover:text-white"
              >
                {describeFilter(filter, columns)}
              </button>
              <button
                type="button"
                aria-label={`Remove the filter on ${filter.column}`}
                onClick={() => onChange(filters.filter((_, position) => position !== index))}
                className="b4-focus flex-none w-4 h-4 mr-1 rounded-[3px] leading-none hover:bg-action/25 hover:text-white"
              >
                ✕
              </button>
            </span>
          </span>
        ))}

        {/* The rest of the bar is the click target, so it has to be the element
            that carries the handler — hence the empty growing span. */}
        <span
          onClick={openAtColumn}
          className={`flex-1 self-stretch flex items-center text-ds-135 text-navy-300 ${
            isCompact ? 'min-w-[4ch] truncate' : 'min-w-[10ch]'
          }`}
        >
          {filters.length
            ? ''
            : isCompact
              // Two column names do not fit beside a menu and a green button.
              ? 'Filter…'
              : `Filter by ${columns.slice(1, 3).map(column => column.name).join(', ') || 'column'}…`}
        </span>

        {filters.length > 1 ? (
          <button
            type="button"
            aria-label="Clear every filter"
            onClick={() => onChange([])}
            className="b4-focus flex-none h-[22px] px-2 rounded-[5px] font-courrier text-ds-115 text-navy-300 hover:text-white hover:bg-navy-600"
          >
            Clear
          </button>
        ) : null}
      </div>

      {step?.at === 'column' ? (
        <Popover width={300}>
          <div className="px-2.5 pt-2.5 pb-1.5">
            <SearchField
              ref={searchRef}
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search columns…"
              aria-label="Search columns"
              prefix={<SearchIcon size={16} />}
              className="h-[34px]"
              inputClassName="font-courrier"
            />
          </div>
          <div className="max-h-[280px] overflow-auto b4-thin">
            {shown.map(column => (
              <PopoverItem
                key={column.name}
                icon={<ChevronRightIcon size={16} />}
                meta={<span className="flex-none font-courrier text-ds-115 text-navy-300">{shortTypeName(column.dataType)}</span>}
                onClick={() => setStep({ at: 'operator', column })}
              >
                <span className="font-courrier">{column.name}</span>
              </PopoverItem>
            ))}
            {shown.length === 0 ? (
              <span className="block px-3.5 py-3 text-ds-125 text-navy-300">No column matches that.</span>
            ) : null}
          </div>
        </Popover>
      ) : step?.at === 'operator' ? (
        <Popover width={300}>
          <button
            type="button"
            onClick={openAtColumn}
            className="b4-focus flex items-center gap-2 w-full px-3.5 py-2.5 text-left border-b border-navy-600 hover:bg-navy-600"
          >
            <span className="flex-none text-navy-300 rotate-180"><ChevronRightIcon size={14} /></span>
            <span className="min-w-0 truncate font-courrier text-ds-125 text-white">{step.column.name}</span>
            <span className="flex-none ml-auto font-courrier text-ds-115 text-navy-300">
              {shortTypeName(step.column.dataType)}
            </span>
          </button>
          <div className="max-h-[280px] overflow-auto b4-thin">
            {operatorsFor(step.column.dataType).map(operator => (
              <PopoverItem
                key={operator.label}
                onClick={() => pickOperator(step.column, operator)}
              >
                {operator.label}
              </PopoverItem>
            ))}
          </div>
        </Popover>
      ) : step?.at === 'value' ? (
        <Popover width={300}>
          <button
            type="button"
            onClick={() => setStep({ at: 'operator', column: step.column })}
            className="b4-focus flex items-center gap-2 w-full px-3.5 py-2.5 text-left border-b border-navy-600 hover:bg-navy-600"
          >
            <span className="flex-none text-navy-300 rotate-180"><ChevronRightIcon size={14} /></span>
            <span className="min-w-0 truncate font-courrier text-ds-125 text-white">
              {step.column.name} {step.operator.label}
            </span>
          </button>
          {/* The box, and nothing else in it: this step has one question. */}
          <form
            onSubmit={event => {
              event.preventDefault();
              if (draft.trim()) {
                put({ column: step.column.name, operator: step.operator.id, value: draft.trim() }, step.editing);
              }
            }}
            className="flex flex-col gap-2.5 p-2.5"
          >
            <SearchField
              ref={valueRef}
              value={draft}
              onChange={event => setDraft(event.target.value)}
              placeholder={step.operator.hint || 'Value…'}
              aria-label={`Value for ${step.column.name}`}
              className="h-[34px]"
              inputClassName="font-courrier"
            />
            <div className="flex items-center justify-between gap-2">
              <SectionLabel>{shortTypeName(step.column.dataType)}</SectionLabel>
              <DsButton variant="primary" size="md" isDisabled={!draft.trim()} onClick={() => {
                if (draft.trim()) {
                  put({ column: step.column.name, operator: step.operator.id, value: draft.trim() }, step.editing);
                }
              }}>
                {typeof step.editing === 'number' ? 'Update' : 'Apply'}
              </DsButton>
            </div>
          </form>
        </Popover>
      ) : null}
    </div>
  );
};

export default FilterInput;
